// src/utils/partialPaymentUtil.js
//
// Everything the "Confirm (Partial)" flow needs to decide what a guest may
// pay up front, what they still owe, and how that is written to a booking.
//
// The feature is OFF unless Config -> Pricing -> "Partial Payment Allowed
// (%)" holds a number above zero. With it off, isPartialPaymentEnabled is
// false everywhere, the button never renders, and nothing else in this file
// is ever reached — which is what keeps every existing deployment behaving
// exactly as it did before.

import {
  DEFAULT_PARTIAL_PAYMENT_PERCENT,
  MAX_PARTIAL_PAYMENT_PERCENT,
  PARTIAL_PAYMENT_PERCENT_TAG,
  PARTIAL_PAYMENT_TAG,
  PAYMENT_STATUS_PARTIAL,
  PAYMENT_STATUS_PAID,
} from '../constants/bookingConst';
import { formatMoney } from './currencyUtil';
import { ref, update } from 'firebase/database';

/** Money is compared and stored to two decimal places, never raw floats. */
const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * The minimum percentage of the total a guest must pay for a partial
 * confirmation, from Config -> Pricing.
 *
 * Anything blank, non-numeric, negative or over 100 resolves to 0 — i.e.
 * "full payment required" — so a mistyped config can never let a booking
 * through for less than it should be.
 *
 * @param {object} [config] - the club config (ConfigContext).
 * @returns {number} 0-100.
 */
export const getPartialPaymentPercent = (config) => {
  const percent = Number(config?.partialPaymentPercent);
  if (!Number.isFinite(percent) || percent <= 0) return DEFAULT_PARTIAL_PAYMENT_PERCENT;
  return Math.min(percent, MAX_PARTIAL_PAYMENT_PERCENT);
};

/**
 * Whether this club accepts downpayments at all.
 *
 * @param {object} [config]
 * @returns {boolean}
 */
export const isPartialPaymentEnabled = (config) => getPartialPaymentPercent(config) > 0;

/**
 * What the whole reservation costs.
 *
 * A multi-date booking is stored as one record PER DATE (see
 * bookingGroupUtil.js) and only the PRIMARY record's `paymentAmount` is the
 * reservation's grand total — the others carry their own date's court
 * subtotal. So the primary is used when the group's records are in hand, and
 * the record itself otherwise (which is correct for an ordinary single-date
 * booking, the overwhelming majority).
 *
 * @param {object} booking - a booking record, or a grouped entry.
 * @param {Array<object>} [groupRecords] - every record in the group, if fetched.
 * @returns {number}
 */
export const getReservationTotal = (booking, groupRecords = []) => {
  const primary = (groupRecords || []).find(rec => rec?.groupPrimary) || null;
  const source = primary || booking;
  return roundMoney(source?.paymentAmount);
};

/**
 * The smallest amount that may be accepted as a downpayment.
 *
 * Rounded UP to the nearest whole peso (20% of ₱22 is ₱5, not ₱4.40), so
 * guests pay a round figure and a booking is never confirmed short of its own
 * rule. Never more than the total itself, for a total under one peso or not a
 * whole number at 100%.
 *
 * @param {number} total - the reservation total.
 * @param {number} percent - see getPartialPaymentPercent.
 * @returns {number}
 */
export const getMinimumPartialAmount = (total, percent) => {
  const roundedTotal = roundMoney(total);
  // Trimmed to 6 decimals before rounding up: floating point can leave an
  // exact share a hair over a whole number (e.g. 11.000000000000002), which
  // a plain ceil would turn into ₱12 for an exact ₱11.
  const share = Number(((roundedTotal * (Number(percent) || 0)) / 100).toFixed(6));
  return Math.min(Math.ceil(share), roundedTotal);
};

/**
 * How a booking stands on payment, in the one shape every screen renders.
 *
 * `isPartial` is the only thing tested for anywhere: a record from before
 * this feature existed has no paymentStatus at all and reads as not-partial,
 * exactly as it should.
 *
 * @param {object} [booking] - a booking record or grouped entry.
 * @returns {{isPartial: boolean, amountPaid: number, balanceDue: number}}
 */
export const getBookingPaymentState = (booking) => {
  const isPartial = booking?.paymentStatus === PAYMENT_STATUS_PARTIAL;
  return {
    isPartial,
    amountPaid: roundMoney(booking?.amountPaid),
    balanceDue: roundMoney(booking?.balanceDue),
  };
};

/**
 * Validates what the admin typed into the partial-payment field.
 *
 * Returns a consistent result object rather than throwing: the modal renders
 * `error` inline under the input and only submits on `ok`.
 *
 * @param {string|number} value - the raw field value.
 * @param {number} total - the reservation total.
 * @param {number} percent - the configured minimum percentage.
 * @param {object} [config] - the club config, for the currency symbol in messages.
 * @returns {{ok: boolean, amount: number, error: string}}
 */
export const validatePartialAmount = (value, total, percent, config) => {
  const fail = (error) => ({ ok: false, amount: 0, error });

  if (value === '' || value === null || value === undefined) {
    return fail('Enter the amount received.');
  }
  const amount = roundMoney(Number.parseFloat(value));
  if (!Number.isFinite(Number.parseFloat(value)) || amount <= 0) {
    return fail('Enter a valid amount.');
  }

  const reservationTotal = roundMoney(total);
  if (reservationTotal <= 0) {
    return fail('This reservation has no total to pay against.');
  }

  const minimum = getMinimumPartialAmount(reservationTotal, percent);
  if (amount < minimum) {
    return fail(`Must be at least ${formatMoney(minimum, config)} (${percent}% of ${formatMoney(reservationTotal, config)}).`);
  }
  if (amount > reservationTotal) {
    return fail(`Cannot be more than the ${formatMoney(reservationTotal, config)} total.`);
  }

  return { ok: true, amount, error: '' };
};

/**
 * The fields written to every date-record of the reservation when a
 * downpayment is accepted.
 *
 * Mirrored across the group rather than kept on the primary alone — the same
 * thing `bookingFee` does — so any date's grid cell can show the indicator
 * without first resolving which record is the primary. They describe the
 * RESERVATION, so they must not be summed across a group.
 *
 * An amount that covers the whole total is recorded as a full payment: the
 * admin typed the total into the partial field, and storing that as "partial
 * with a ₱0 balance" would leave a badge on a reservation that owes nothing.
 *
 * @param {number} total - the reservation total.
 * @param {number} amountPaid - what was actually received.
 * @param {number} percent - the percentage in force when it was taken, kept
 *   for the record since the config can change afterwards.
 * @param {string} at - ISO timestamp of the payment (club time).
 * @returns {object} field/value pairs for updateGroupStatus's `extra`.
 */
export const buildPartialPaymentFields = (total, amountPaid, percent, at) => {
  const reservationTotal = roundMoney(total);
  const paid = Math.min(roundMoney(amountPaid), reservationTotal);
  const balanceDue = roundMoney(reservationTotal - paid);
  const settled = balanceDue <= 0;

  return {
    paymentStatus: settled ? PAYMENT_STATUS_PAID : PAYMENT_STATUS_PARTIAL,
    amountPaid: paid,
    balanceDue,
    partialPaymentPercent: settled ? null : (Number(percent) || 0),
    paymentUpdatedAt: at,
  };
};

/**
 * The fields written when the remaining balance is settled ("Mark Fully
 * Paid"). `partialPaymentPercent` is cleared to null, which Firebase stores
 * as a deletion, so a settled reservation carries no leftover partial state.
 *
 * @param {number} total - the reservation total.
 * @param {string} at - ISO timestamp (club time).
 * @returns {object}
 */
export const buildFullPaymentFields = (total, at) => ({
  paymentStatus: PAYMENT_STATUS_PAID,
  amountPaid: roundMoney(total),
  balanceDue: 0,
  partialPaymentPercent: null,
  paymentUpdatedAt: at,
});

/**
 * The booking fields recording the guest's own "Partial payment" tick on
 * Booking details / checkout — what they said they would pay, as opposed to
 * what the admin later confirmed (buildPartialPaymentFields).
 *
 * The percentage is captured with the choice so the admin sees the rule the
 * guest agreed to, even if Config -> Pricing changes afterwards.
 *
 * @param {boolean} checked - whether the guest ticked the box
 * @param {object} config - the club config
 * @returns {{guestPartialPayment: boolean, guestPartialPercent: number|null}}
 */
export const buildGuestPartialPaymentFields = (checked, config) => {
  const chosen = Boolean(checked) && isPartialPaymentEnabled(config);
  return {
    guestPartialPayment: chosen,
    guestPartialPercent: chosen ? getPartialPaymentPercent(config) : null,
  };
};

/**
 * The partial amount a guest chose to pay, from the choice saved on their
 * booking (guestPartialPayment / guestPartialPercent) — or null when there is
 * none to show.
 *
 * Null as well whenever Config -> Pricing -> "Partial Payment Allowed (%)" is
 * 0 or empty: the feature is hidden everywhere then, including on bookings
 * that chose it while it was on.
 *
 * Uses the percentage saved with the choice (what the guest agreed to) and
 * the same rounding-up rule as Confirm (Partial).
 *
 * @param {object} [booking] - a booking record, grouped entry or the
 *   checkout's combined booking data; its paymentAmount is the total
 * @param {object} [config] - the club config
 * @returns {number|null}
 */
export const getGuestPartialAmount = (booking, config) => {
  if (!isPartialPaymentEnabled(config) || !booking?.guestPartialPayment) return null;
  const percent = Number(booking.guestPartialPercent);
  if (!Number.isFinite(percent) || percent <= 0) return null;
  return getMinimumPartialAmount(Number(booking.paymentAmount || 0), percent);
};

/**
 * The partial-payment figures for a booking email — amount paid, what is
 * still owed, and the total — or null for an ordinary full payment, in which
 * case the email shows only its usual single amount.
 *
 * Two ways a booking becomes partial, checked in this order:
 *  1. The admin recorded a downpayment with "Confirm (Partial)"
 *     (paymentStatus 'partial'): the real amount received and balance. No
 *     percentage, since the admin may have taken more than the minimum.
 *  2. The guest ticked "Partial payment" at checkout: their share at the
 *     percentage saved with the choice (getGuestPartialAmount).
 *
 * Null whenever "Partial Payment Allowed (%)" is 0 or empty, like every other
 * partial-payment display.
 *
 * @param {object} [booking] - the booking data being emailed; paymentAmount
 *   is the reservation total
 * @param {object} [config] - the club config
 * @returns {{amountPaid: number, balance: number, total: number, percent: number|null}|null}
 */
export const getPaymentBreakdown = (booking, config) => {
  if (!isPartialPaymentEnabled(config) || !booking) return null;

  if (booking.paymentStatus === PAYMENT_STATUS_PARTIAL) {
    const amountPaid = roundMoney(booking.amountPaid);
    const balance = roundMoney(booking.balanceDue);
    if (balance > 0) return { amountPaid, balance, total: roundMoney(amountPaid + balance), percent: null };
  }

  const guestAmount = getGuestPartialAmount(booking, config);
  if (guestAmount === null) return null;
  const total = roundMoney(booking.paymentAmount);
  return {
    amountPaid: guestAmount,
    balance: Math.max(0, roundMoney(total - guestAmount)),
    total,
    percent: Number(booking.guestPartialPercent),
  };
};

/**
 * The short tag printed beside "Total Paid" for a partial payment —
 * "20% partial", or just "partial" when no percentage applies.
 *
 * @param {{percent: number|null}} breakdown - from getPaymentBreakdown
 * @returns {string}
 */
export const describePartialPaymentTag = (breakdown) => {
  const percent = Number(breakdown?.percent);
  return Number.isFinite(percent) && percent > 0
    ? PARTIAL_PAYMENT_PERCENT_TAG.replace('{percent}', percent)
    : PARTIAL_PAYMENT_TAG;
};

/**
 * Writes the guest's final partial-payment choice onto every date record of
 * the booking, at the moment the receipt submits it.
 *
 * @param {object} db - Firebase database
 * @param {string[]} bookingKeys - one record id per date
 * @param {{guestPartialPayment: boolean, guestPartialPercent: number|null}} fields
 * @returns {Promise<void>}
 * @throws {Error} when the write is rejected — the caller decides whether to
 *   block on it.
 */
export const saveGuestPartialPaymentChoice = async (db, bookingKeys, fields) => {
  if (!db || !Array.isArray(bookingKeys) || bookingKeys.length === 0) return;
  const updates = {};
  bookingKeys.forEach((key) => {
    updates[`bookings/${key}/guestPartialPayment`] = fields.guestPartialPayment;
    updates[`bookings/${key}/guestPartialPercent`] = fields.guestPartialPercent;
  });
  await update(ref(db), updates);
};
